import { call, put, takeLatest } from 'redux-saga/effects';
import api from 'guardian/api';

export const types = {
  CHECK_SESSION: 'AUTH/CHECK_SESSION',
  CHECK_SESSION_SUCCESS: 'AUTH/CHECK_SESSION_SUCCESS',
  CHECK_SESSION_FAILURE: 'AUTH/CHECK_SESSION_FAILURE',
  LOGOUT: 'AUTH/LOGOUT',
  LOGOUT_SUCCESS: 'AUTH/LOGOUT_SUCCESS',
  SWITCH_ACCOUNT: 'AUTH/SWITCH_ACCOUNT',
  SWITCH_ACCOUNT_SUCCESS: 'AUTH/SWITCH_ACCOUNT_SUCCESS',
  SWITCH_ACCOUNT_FAILURE: 'AUTH/SWITCH_ACCOUNT_FAILURE',
  CLEAR_ERRORS: 'AUTH/CLEAR_ERRORS',
  SESSION_EXPIRED: 'AUTH/SESSION_EXPIRED',
};

export const actions = {
  checkSession: () => ({ type: types.CHECK_SESSION }),
  logout: () => ({ type: types.LOGOUT }),
  switchAccount: (accountId) => ({ type: types.SWITCH_ACCOUNT, accountId }),
  clearErrors: () => ({ type: types.CLEAR_ERRORS }),
  sessionExpired: () => ({ type: types.SESSION_EXPIRED }),
};

// Named export for direct import by the axios interceptor
export const sessionExpired = actions.sessionExpired;

const initialState = {
  isAuthenticated: false,
  isLoading: true,
  authDisabled: false,
  user: null,
  account: null,
  accounts: [],
  adminLogin: false,
  role: null,
  errors: null,
  csrfToken: null,
};

export default function authReducer(state = initialState, action) {
  switch (action.type) {
    case types.CHECK_SESSION:
      return { ...state, isLoading: true };
    case types.CHECK_SESSION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: action.isAuthenticated,
        authDisabled: action.authDisabled || false,
        user: action.user,
        account: action.account,
        accounts: action.accounts,
        adminLogin: action.adminLogin,
        role: action.role || null,
        csrfToken: action.csrfToken,
      };
    case types.CHECK_SESSION_FAILURE:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        account: null,
        accounts: [],
        role: null,
      };
    case types.LOGOUT_SUCCESS:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        account: null,
        accounts: [],
        role: null,
      };
    case types.SWITCH_ACCOUNT:
      return { ...state, isLoading: true };
    case types.SWITCH_ACCOUNT_SUCCESS:
      return {
        ...state,
        isLoading: false,
        account: action.account,
      };
    case types.SWITCH_ACCOUNT_FAILURE:
      return {
        ...state,
        isLoading: false,
        errors: action.errors,
      };
    case types.CLEAR_ERRORS:
      return { ...state, errors: null };
    case types.SESSION_EXPIRED:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        account: null,
        accounts: [],
        role: null,
      };
    default:
      return state;
  }
}

// Fetch /session/ (CSRF + auth state) first; if authed, fetch /me/ for RBAC
// payload. /me/ is auth-required (returns 401 when not authed), so calling
// it unconditionally would trigger the axios 401 interceptor and loop the
// user back through /login on every boot.
function* checkSessionSaga() {
  try {
    const sessionRes = yield call(api.get, '/api/v1/auth/api/session/');
    const session = sessionRes.data;

    if (!session.isAuthenticated) {
      yield put({
        type: types.CHECK_SESSION_SUCCESS,
        isAuthenticated: false,
        authDisabled: session.authDisabled || false,
        user: null,
        account: null,
        accounts: [],
        adminLogin: false,
        role: null,
        csrfToken: session.csrfToken,
      });
      return;
    }

    const meRes = yield call(api.get, '/api/v1/auth/api/me/');
    const me = meRes.data;
    // Clear the single-redirect guard so a future session expiry can redirect again.
    window.__sessionExpiredRedirect = undefined;
    yield put({
      type: types.CHECK_SESSION_SUCCESS,
      isAuthenticated: true,
      authDisabled: session.authDisabled || false,
      user: me.user,
      account: me.account,
      accounts: me.accounts || [],
      adminLogin: session.adminLogin || false,
      role: me.role || null,
      csrfToken: session.csrfToken,
    });
  } catch (error) {
    yield put({ type: types.CHECK_SESSION_FAILURE });
  }
}

function* logoutSaga() {
  try {
    yield call(api.get, '/api/v1/auth/logout/');
    yield put({ type: types.LOGOUT_SUCCESS });
    window.location.href = '/login';
  } catch (error) {
    yield put({ type: types.LOGOUT_SUCCESS });
    window.location.href = '/login';
  }
}

function* switchAccountSaga(action) {
  try {
    const response = yield call(api.post, '/api/v1/auth/switch-account/', {
      account_id: action.accountId,
    });
    yield put({
      type: types.SWITCH_ACCOUNT_SUCCESS,
      account: response.data.account,
    });
    window.location.reload();
  } catch (error) {
    const errors = error.response?.data?.error || 'Failed to switch account';
    yield put({ type: types.SWITCH_ACCOUNT_FAILURE, errors: { general: [errors] } });
  }
}

export function* authSagas() {
  yield takeLatest(types.CHECK_SESSION, checkSessionSaga);
  yield takeLatest(types.LOGOUT, logoutSaga);
  yield takeLatest(types.SWITCH_ACCOUNT, switchAccountSaga);
}
